// Copyright (c) 2016 Deere & Company

import AmountCellRenderer from '../../shared/report-cell-renderers/amount-cell-renderer';
import ClaimNumberCellRenderer from '../../shared/report-cell-renderers/claim-number-cell-renderer';
import CreditMemoCellRenderer from '../../shared/report-cell-renderers/credit-memo-cell-renderer';
import DateCellRenderer from '../../shared/report-cell-renderers/date-cell-renderer';
import PinCellRenderer from '../../shared/report-cell-renderers/pin-cell-renderer';
import classNames from '../../shared/constants/ag-grid-class-names';
import dateFilterParams from '../../shared/constants/date-filter-params';
import {translate} from '../../../shared/services/translation-service';

const getInvoiceRequestColumn = userPrivacy =>
    userPrivacy.cmInvoiceRequestNotAllowed ?
        null : {
            cellClass: classNames.cellLeftClassName,
            colId: 'invoiceRequest',
            field: 'invoiceRequest',
            headerClass: classNames.headerLeftClassName,
            headerName: translate('INVOICE_REQUEST'),
            width: 150
        };

const getInvoiceNumberColumn = userPrivacy =>
    userPrivacy.cmInvoiceRequestNotAllowed ?
        null : {
            cellClass: classNames.cellLeftClassName,
            colId: 'dealerInvoiceNumber',
            field: 'dealerInvoiceNumber',
            headerClass: classNames.headerLeftClassName,
            headerName: translate('INVOICE_NUMBER'),
            width: 150
        };

const getTechIdColumn = userPrivacy =>
    userPrivacy.cmShowTechId ?
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'techId',
        field: 'techId',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('TECH_ID'),
        width: 150
    } : null;

export const getCreditMemoColumnDefinitions = (userPrivacy, isSapUp = true) => [
    {
        cellClass: classNames.cellLeftClassName,
        cellRendererFramework: isSapUp ? CreditMemoCellRenderer : null,
        colId: 'creditMemoNumber',
        field: 'creditMemoNumber',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('CREDIT_MEMO'),
        width: 200
    },
    {
        cellClass: classNames.cellLeftClassName,
        cellRendererFramework: isSapUp ? ClaimNumberCellRenderer : null,
        colId: 'claimNumber',
        field: 'claimNumber',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('CLAIM_NUMBER'),
        width: 150
    },
    getInvoiceRequestColumn(userPrivacy),
    getInvoiceNumberColumn(userPrivacy),
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'pipNumber',
        field: 'pipNumber',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('PIP_NUMBER'),
        width: 150
    },
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'accountNumber',
        field: 'accountNumber',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('DEALER_ACCOUNT'),
        width: 150
    },
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'dealerLocation',
        field: 'responsibleAccount.location',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('DEALER_LOCATION'),
        valueGetter: params => `${params.data.accountCity}, ${params.data.accountState}`,
        width: 220
    },
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'leadAccount',
        field: 'leadAccount',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('LEAD_ACCOUNT'),
        width: 150
    },
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'payer',
        field: 'payer',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('PAYER_ACCOUNT'),
        width: 150
    },
    {
        cellClass: classNames.cellLeftClassName,
        cellRendererFramework: PinCellRenderer,
        colId: 'pin',
        field: 'pin',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('PIN'),
        width: 175
    },
    {
        cellClass: classNames.cellClassName,
        cellRendererFramework: DateCellRenderer,
        colId: 'failureDate',
        field: 'failureDate',
        filter: 'date',
        filterParams: dateFilterParams,
        headerClass: classNames.headerClassName,
        headerName: translate('FAILED'),
        width: 150
    },
    {
        cellClass: classNames.cellClassName,
        cellRendererFramework: DateCellRenderer,
        colId: 'repairDate',
        field: 'repairDate',
        filter: 'date',
        filterParams: dateFilterParams,
        headerClass: classNames.headerClassName,
        headerName: translate('REPAIRED'),
        width: 150
    },
    {
        cellClass: classNames.cellClassName,
        cellRendererFramework: DateCellRenderer,
        colId: 'originalSubmissionDate',
        field: 'originalSubmissionDate',
        filter: 'date',
        filterParams: dateFilterParams,
        headerClass: classNames.headerClassName,
        headerName: translate('ORIGINALLY_SUBMITTED'),
        width: 200
    },
    {
        cellClass: classNames.cellClassName,
        cellRendererFramework: DateCellRenderer,
        colId: 'dateIssued',
        field: 'dateIssued',
        filter: 'date',
        filterParams: dateFilterParams,
        headerClass: classNames.headerClassName,
        headerName: translate('ISSUED'),
        width: 150
    },
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'claimTypeDescription',
        field: 'claimTypeDescription',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('CLAIM_TYPE'),
        width: 150
    },
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'subClaimTypeDescription',
        field: 'subClaimTypeDescription',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('SUB_CLAIM_TYPE'),
        width: 200
    },
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'programName',
        field: 'programName',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('PROGRAM'),
        valueGetter: params => params.data.programName ? `${params.data.programName} - ${params.data.programDescription}` : '',
        width: 240
    },
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'failurePart',
        field: 'failurePart',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('FAILED_PART'),
        width: 150
    },
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'failurePartDescription',
        field: 'failurePartDescription',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('FAILED_PART_DESCRIPTION'),
        width: 200
    },
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'failureMode',
        field: 'failureMode',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('FAILURE_MODE'),
        width: 150
    },
    {
        cellClass: classNames.cellRightClassName,
        colId: 'machineUse',
        field: 'machineUse',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('AMOUNT_OF_USE'),
        width: 150
    },
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'unitOfMeasure',
        field: 'unitOfMeasure',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('UNIT_OF_MEASURE'),
        width: 150
    },
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'referenceNumber1',
        field: 'referenceNumber1',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('WORK_ORDER_ONE'),
        width: 200
    },
    getTechIdColumn(userPrivacy),
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'repairNumber',
        field: 'repairNumber',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('REPAIR_NUMBER'),
        width: 150
    },
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'referenceNumber2',
        field: 'referenceNumber2',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('WORK_ORDER_TWO'),
        width: 200
    },
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'warrantyType',
        field: 'warrantyType',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('WARRANTY_TYPE'),
        width: 150
    },
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'planName',
        field: 'planName',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('PLAN_NAME'),
        width: 200
    },
    {
        cellClass: classNames.cellClassName,
        cellRendererFramework: DateCellRenderer,
        colId: 'deliveryDate',
        field: 'deliveryDate',
        filter: 'date',
        filterParams: dateFilterParams,
        headerClass: classNames.headerClassName,
        headerName: translate('DELIVERED'),
        width: 150
    },
    {
        cellClass: classNames.cellClassName,
        cellRendererFramework: DateCellRenderer,
        colId: 'basicWarrantyStartDate',
        field: 'basicWarrantyStartDate',
        filter: 'date',
        filterParams: dateFilterParams,
        headerClass: classNames.headerClassName,
        headerName: translate('BASIC_WARRANTY_STARTS'),
        width: 225
    },
    {
        cellClass: classNames.cellClassName,
        cellRendererFramework: DateCellRenderer,
        colId: 'basicWarrantyEndDate',
        field: 'basicWarrantyEndDate',
        filter: 'date',
        filterParams: dateFilterParams,
        headerClass: classNames.headerClassName,
        headerName: translate('BASIC_WARRANTY_ENDS'),
        width: 225
    },
    {
        cellClass: classNames.cellLeftClassName,
        colId: 'customerName',
        field: 'customerName',
        headerClass: classNames.headerLeftClassName,
        headerName: translate('CUSTOMER_NAME'),
        width: 200
    },
    {
        cellClass: classNames.cellClassName,
        colId: 'currency',
        field: 'currency',
        headerClass: classNames.headerClassName,
        headerName: translate('CURRENCY'),
        width: 130
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'requestedParts',
        field: 'requestedParts',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('REQUESTED_PARTS'),
        width: 150
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'approvedParts',
        field: 'approvedParts',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('APPROVED_PARTS'),
        width: 150
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'unpaidParts',
        field: 'unpaidParts',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('UNPAID_PARTS'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'approvedPartsTotal',
        field: 'approvedPartsTotal',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('APPROVED_PARTS_TOTAL'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'approvedPartsAdditive',
        field: 'approvedPartsAdditive',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('APPROVED_PARTS_ADDITIVE'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'unpaidPartsAdditive',
        field: 'unpaidPartsAdditive',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('UNPAID_PARTS_ADDITIVE'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'approvedOnshelfCost',
        field: 'approvedOnshelfCost',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('APPROVED_ON_SHELF_COST'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'approvedCoreValue',
        field: 'approvedCoreValue',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('APPROVED_CORE_VALUE'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'requestedLaborHours',
        field: 'requestedLaborHours',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('REQUESTED_LABOR_HOURS'),
        valueGetter: params => params.data.requestedLaborHours ? params.data.requestedLaborHours : 0,
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'requestedLaborAmount',
        field: 'requestedLaborAmount',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('REQUESTED_LABOR_AMOUNT'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'approvedLaborHours',
        field: 'approvedLaborHours',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('APPROVED_LABOR_HOURS'),
        valueGetter: params => params.data.approvedLaborHours ? params.data.approvedLaborHours : 0,
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'approvedLaborAmount',
        field: 'approvedLaborAmount',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('APPROVED_LABOR_AMOUNT'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'approvedLaborAdditive',
        field: 'approvedLaborAdditive',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('APPROVED_LABOR_ADDITIVE'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'unpaidLabor',
        field: 'unpaidLabor',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('UNPAID_LABOR'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'pipsUnpaidLaborAdditives',
        field: 'pipsUnpaidLaborAdditives',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('UNPAID_LABOR_ADDITIVES_PIPS'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'unpaidLaborAdditivesExcludingPips',
        field: 'unpaidLaborAdditivesExcludingPips',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('UNPAID_LABOR_ADDITIVES_EXCLUDING_PIPS'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'approvedLaborTotal',
        field: 'approvedLaborTotal',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('APPROVED_LABOR_TOTAL'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'requestRepairHours',
        field: 'requestRepairHours',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('REQUESTED_REPAIR_HOURS'),
        valueGetter: params => params.data.requestRepairHours ? params.data.requestRepairHours : 0,
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'approvedRepairHours',
        field: 'approvedRepairHours',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('APPROVED_REPAIR_HOURS'),
        valueGetter: params => params.data.approvedRepairHours ? params.data.approvedRepairHours : 0,
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'requestRepairAmount',
        field: 'requestRepairAmount',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('REQUESTED_REPAIR_AMOUNT'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'approvedRepairAmount',
        field: 'approvedRepairAmount',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('APPROVED_REPAIR_AMOUNT'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'requestDiagnosticHours',
        field: 'requestDiagnosticHours',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('REQUESTED_DIAGNOSTIC_HOURS'),
        valueGetter: params => params.data.requestDiagnosticHours ? params.data.requestDiagnosticHours : 0,
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'approvedDiagnosticHours',
        field: 'approvedDiagnosticHours',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('APPROVED_DIAGNOSTIC_HOURS'),
        valueGetter: params => params.data.approvedDiagnosticHours ? params.data.approvedDiagnosticHours : 0,
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'requestDiagnosticAmount',
        field: 'requestDiagnosticAmount',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('REQUESTED_DIAGNOSTIC_AMOUNT'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'approvedDiagnosticAmount',
        field: 'approvedDiagnosticAmount',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('APPROVED_DIAGNOSTIC_AMOUNT'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'requestedOtherCredits',
        field: 'requestedOtherCredits',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('REQUESTED_OTHER_CREDITS'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'approvedOtherCredits',
        field: 'approvedOtherCredits',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('APPROVED_OTHER_CREDITS'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'unpaidOtherCredits',
        field: 'unpaidOtherCredits',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('UNPAID_OTHER_CREDITS'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'requestedTravelTotal',
        field: 'requestedTravelTotal',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('REQUESTED_TRAVEL_TOTAL'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'approvedTravelTotal',
        field: 'approvedTravelTotal',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('APPROVED_TRAVEL_TOTAL'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'unpaidTravel',
        field: 'unpaidTravel',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('UNPAID_TRAVEL'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'approvedTaxesTotal',
        field: 'approvedTaxesTotal',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('APPROVED_TAXES_TOTAL'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'deductible',
        field: 'deductible',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('DEDUCTIBLE'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'totalUnpaid',
        field: 'totalUnpaid',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('TOTAL_UNPAID'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'totalRequestedClaimValue',
        field: 'totalRequestedClaimValue',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('TOTAL_REQUESTED_CLAIM_VALUE'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'totalApprovedClaimValue',
        field: 'totalApprovedClaimValue',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('TOTAL_APPROVED_CLAIM_VALUE'),
        width: 200
    },
    {
        cellClass: classNames.cellRightClassName,
        cellRendererFramework: AmountCellRenderer,
        colId: 'totalApprovedIncludingTaxes',
        field: 'totalApprovedIncludingTaxes',
        filter: 'number',
        headerClass: classNames.headerRightClassName,
        headerName: translate('TOTAL_APPROVED_INCLUDING_TAXES'),
        width: 225
    }
].filter(column => column !== null);



// WEBPACK FOOTER //
// ./src/reports/credit-memo/report-services/column-definitions.js